import { LocaleType } from '../../constants/localeConstants';
import { DATE_GROUPS } from '../../inputPatterns/date/constants';
import { DateFormatType } from '../../inputPatterns/date/formats';
import { DateParts } from '../../types';
import { isStrictMatch } from '../../utils/internal/isStrictMatch';
import { parseUnitInRange } from '../../utils/internal/parseUnitInRange';
import { getBestDateMatch, isMonthYearFormat } from './helpers/dateHelpers';
import { getMonthIndex } from './helpers/monthHelpers';

type ExtractDateComponentsArgs = {
  value: string;
  supportedFormats: DateFormatType[];
  locale: LocaleType;
  isStrictMode?: boolean;
};
/**
 * Extracts date components (year, month, day) based on recognized date formats.
 *
 * @param {string} value - The date string to extract from.
 * @param {LocaleType} locale - The locale used for determining month position (e.g., "en-US").
 * @returns {{ year: number; month: number; day?: number } | undefined} Extracted date parts or `undefined` if invalid.
 */
export const extractDateComponents = ({
  value,
  supportedFormats,
  locale,
  isStrictMode = true,
}: ExtractDateComponentsArgs): DateParts | undefined => {
  const dateMatch = getBestDateMatch(value, supportedFormats);
  if (!dateMatch || (isStrictMode && !isStrictMatch(value.trim(), dateMatch.match))) return undefined; // No match, invalid format

  const { matchedGroups, label } = dateMatch;
  if (!matchedGroups) return undefined; // No match, invalid format

  const {
    [DATE_GROUPS.NUMERIC_MONTH]: numericMonth,
    [DATE_GROUPS.MONTH_NAME_FULL]: fullMonth,
    [DATE_GROUPS.MONTH_NAME_SHORT]: shortMonth,
    [DATE_GROUPS.DAY]: day,
    [DATE_GROUPS.YEAR]: year,
  } = matchedGroups;

  const zeroBasedMonthIndex = getMonthIndex({ numericMonth, month: fullMonth || shortMonth, locale });
  const numericYear = Number(year) ?? -1;
  const numericDay = isMonthYearFormat(label) ? 1 : parseUnitInRange(day, 1, 31) ?? -1; // Default to 1 if month year format detected
  return {
    year: numericYear,
    month: zeroBasedMonthIndex ?? -1,
    day: numericDay,
  };
};
